import React, { useEffect, useMemo, useState, useRef, useDeferredValue } from "react";
import * as XLSX from "xlsx";
import "./WalletWithdrawalList.css";
import WalletAllocationPopup from "./WalletAllocationPopup";
import AadhaarDetailsPopup from "./AadhaarDetailsPopup";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

function getNumericUserId(item = {}) {
  // Prefer explicit numeric up_users.id fields. Some API rows also contain a
  // composite label such as "user[64305]-wallet-160.0-job[50986]".
  const candidates = [
    item.up_users_id,
    item.up_user_id,
    item.user_id,
    item.userId,
    item.owner_id,
    item.ownerId,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    if (typeof candidate === "number" && Number.isInteger(candidate) && candidate > 0) {
      return candidate;
    }
    const value = String(candidate).trim();
    if (/^\d+$/.test(value) && Number(value) > 0) return Number(value);
    const match = value.match(/\buser\s*\[\s*(\d+)\s*\]/i);
    if (match && Number(match[1]) > 0) return Number(match[1]);
  }
  return null;
}

function normalizeWalletItem(item = {}) {
  const bank = item.bank_details ?? item.bankDetails ?? "";
  const actualUserId = getNumericUserId(item);

  return {
    // Preserve the original API row primary key used by allocation-preview.
    transactionId:
      item.id ??
      item.transaction_id ??
      item.transactionId ??
      null,
    walletId: item.wallet_id ?? item.walletId ?? item.id ?? "-",
    userId: actualUserId ?? "-",
    // Do not substitute a withdrawal-request ID for the transaction ID.
    withdrawalId:
      item.withdrawal_id ??
      item.withdrawalId ??
      null,
    // Prefer the actual user ID over a composite owner key.
    ownerId: actualUserId,
    withdrawalDate:
      item.withdrawal_date ??
      item.withdrawalDate ??
      item.withdrawal_request_date ??
      item.withdrawalRequestDate ??
      item.created_at ??
      item.createdAt ??
      null,
    walletDate:
      item.wallet_date ??
      item.walletDate ??
      item.created_at ??
      item.createdAt ??
      null,
    type:
      item.type ??
      item.transaction_type ??
      item.transactionType ??
      item.transaction_kind ??
      item.transactionKind ??
      "-",
    withdrawalRequestDate:
      item.withdrawal_request_date ??
      item.withdrawalRequestDate ??
      item.requested_at ??
      item.requestedAt ??
      null,
    status:
      item.status ??
      item.withdrawal_status ??
      item.withdrawalStatus ??
      "PENDING",
    userRealName:
      item.user_real_name ??
      item.userRealName ??
      item.real_name ??
      item.realName ??
      item.name ??
      "-",
    displayName: item.display_name ?? item.displayName ?? item.username ?? "-",
    phoneNumber: item.phone_number ?? item.phoneNumber ?? item.phone ?? "-",
    emailAddress: item.email_address ?? item.emailAddress ?? item.email ?? "-",
    address: item.address ?? item.user_address ?? "-",
    jobName:
      item.job_name ?? item.jobName ?? item.job_title ?? item.jobTitle ?? "-",
    
    jobDetailId:
      item.job_detail_id ??
      item.jobDetailId ??
      item.request_id ??
      item.requestId ??
      null,

    verifiedBy:
      item.verified_by ?? item.verifiedBy ?? item.verifier_name ?? "-",
    placeOfSupply: item.place_of_supply ?? item.placeOfSupply ?? "-",
    bankDetails: bank,
    actualJobPrice:
      item.actual_job_price ??
      item.actualJobPrice ??
      item.job_price ??
      item.jobPrice ??
      null,
    walletAmountPayable:
      item.wallet_amount_payable ??
      item.walletAmountPayable ??
      item.payable_amount ??
      item.payableAmount ??
      item.wallet_amount ??
      item.walletAmount ??
      null,
    withdrawalRequestAmount:
      item.withdrawal_request_amount ??
      item.withdrawalRequestAmount ??
      item.requested_amount ??
      null,
    userPayableAmount:
      item.user_payable_amount ??
      item.userPayableAmount ??
      item.payable_user_amount ??
      null,
    pendingBalance:
      item.pending_balance ??
      item.pendingBalance ??
      item.pending_amount ??
      null,
    platformFee:
      item.platform_fee ??
      item.platformFee ??
      item.fee ??
      null,
    taxableAmount:
      item.taxable_amount ??
      item.taxableAmount ??
      item.taxable_value ??
      null,
    cgst:
      item.cgst ??
      item.cgst_amount ??
      item.cgstAmount ??
      null,
    sgst:
      item.sgst ??
      item.sgst_amount ??
      item.sgstAmount ??
      null,
    igst:
      item.igst ??
      item.igst_amount ??
      item.igstAmount ??
      null,
  };
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const datePart = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return `${datePart} at ${timePart}`;
}

const formatDateForInput = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayRange = (start, end) => {
  if (!start && !end) return "Select Date Range";
  const format = (value) => {
    if (!value) return "Select date";
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  };
  return `${format(start)} - ${format(end)}`;
};

function getISTDateString(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formatter.format(date);
  } catch {
    return null;
  }
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "-";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function getStatusClass(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (["PAID", "COMPLETED", "APPROVED", "SUCCESS"].includes(normalized)) {
    return "status-badge status-success";
  }
  if (["REJECTED", "FAILED", "CANCELLED"].includes(normalized)) {
    return "status-badge status-danger";
  }
  if (["PROCESSING", "IN_PROGRESS"].includes(normalized)) {
    return "status-badge status-processing";
  }
  return "status-badge status-pending";
}

function getTypeClass(type) {
  const normalized = String(type || "").trim().toUpperCase();
  if (["WITHDRAWAL", "PAYOUT"].includes(normalized)) {
    return "type-badge type-withdrawal";
  }
  if (["WALLET"].includes(normalized)) {
    return "type-badge type-wallet";
  }
  if (["DEPOSIT", "CREDIT"].includes(normalized)) {
    return "type-badge type-deposit";
  }
  if (["REFUND", "REVERSAL"].includes(normalized)) {
    return "type-badge type-refund";
  }
  if (["FEE", "CHARGE"].includes(normalized)) {
    return "type-badge type-fee";
  }
  return "type-badge type-default";
}

function BankDetails({ bank }) {
  if (!bank) return <span className="muted-value">-</span>;
  if (typeof bank === "string") {
    return <span className="bank-text-wrap">{bank}</span>;
  }

  const bankName = bank.bank_name ?? bank.bankName ?? bank.name ?? "";
  const accountHolder =
    bank.account_holder_name ??
    bank.accountHolderName ??
    bank.account_name ??
    bank.accountName ??
    "";
  const accountNumber =
    bank.account_number ??
    bank.accountNumber ??
    bank.account_no ??
    bank.accountNo ??
    "";
  const ifsc = bank.ifsc ?? bank.ifsc_code ?? bank.ifscCode ?? "";

  return (
    <div className="bank-details">
      {bankName && <strong>{bankName}</strong>}
      {accountHolder && <span>{accountHolder}</span>}
      {accountNumber && <span>A/C: {accountNumber}</span>}
      {ifsc && <span>IFSC: {ifsc}</span>}
    </div>
  );
}

export default function WalletWithdrawalList() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  // Keep the page's original default: show today's records on first load.
  const [fromDate, setFromDate] = useState(() => formatDateForInput(new Date()));
  const [toDate, setToDate] = useState(() => formatDateForInput(new Date()));
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [tempStartDate, setTempStartDate] = useState(() => formatDateForInput(new Date()));
  const [tempEndDate, setTempEndDate] = useState(() => formatDateForInput(new Date()));
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());

  const datePickerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadWalletWithdrawals() {
      setLoading(true);
      setError("");

      try {
        const jwt = localStorage.getItem("jwt");
        const response = await fetch(
          `${API_BASE_URL}/api/wallet-withdrawals`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        const items = Array.isArray(data)
          ? data
          : data?.items ?? data?.data ?? data?.results ?? [];

        if (!cancelled) {
          const processedItems = items.map((item) => {
            const normalized = normalizeWalletItem(item);
            const bankString =
              typeof normalized.bankDetails === "object" && normalized.bankDetails !== null
                ? Object.values(normalized.bankDetails).join(" ")
                : String(normalized.bankDetails || "");

            const formattedWDate = formatDate(normalized.walletDate);
            const formattedReqDate = formatDate(normalized.withdrawalRequestDate);
            const istDateStr = getISTDateString(normalized.walletDate);

            const searchableText = [
              normalized.walletId,
              normalized.userId,
              formattedWDate,
              formattedReqDate,
              normalized.status,
              normalized.type,
              normalized.userRealName,
              normalized.displayName,
              normalized.phoneNumber,
              normalized.emailAddress,
              normalized.address,
              normalized.jobName,
              normalized.verifiedBy,
              normalized.placeOfSupply,
              bankString,
              normalized.actualJobPrice,
              normalized.walletAmountPayable,
              normalized.withdrawalRequestAmount,
              normalized.userPayableAmount,
              normalized.pendingBalance,
              normalized.platformFee,
              normalized.taxableAmount,
              normalized.cgst,
              normalized.sgst,
              normalized.igst,
            ]
              .join(" ")
              .toLowerCase();

            return {
              ...normalized,
              _searchableText: searchableText,
              _formattedWalletDate: formattedWDate,
              _formattedRequestDate: formattedReqDate,
              _istDateStr: istDateStr,
            };
          });

          setRows(processedItems);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("WALLET WITHDRAWAL LIST ERROR:", err);
          setError(
            err?.message || "Unable to load wallet withdrawal records."
          );
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadWalletWithdrawals();

    return () => {
      cancelled = true;
    };
  }, []);

  const calculatePresetDates = (preset) => {
    const today = new Date();
    const todayStr = formatDateForInput(today);

    if (preset === "today") return { start: todayStr, end: todayStr };
    if (preset === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const date = formatDateForInput(yesterday);
      return { start: date, end: date };
    }
    if (preset === "last7days") {
      const start = new Date();
      start.setDate(start.getDate() - 6);
      return { start: formatDateForInput(start), end: todayStr };
    }
    if (preset === "last30days") {
      const start = new Date();
      start.setDate(start.getDate() - 29);
      return { start: formatDateForInput(start), end: todayStr };
    }
    if (preset === "thisMonth") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: formatDateForInput(start), end: todayStr };
    }
    if (preset === "lastMonth") {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatDateForInput(start), end: formatDateForInput(end) };
    }

    return { start: fromDate, end: toDate };
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    if (preset === "custom") return;

    const { start, end } = calculatePresetDates(preset);
    setFromDate(start);
    setToDate(end);
    setTempStartDate(start);
    setTempEndDate(end);
    setShowDatePicker(false);
  };

  const handleDayClick = (dateStr) => {
    setSelectedPreset("custom");

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(dateStr);
      setTempEndDate("");
    } else if (dateStr < tempStartDate) {
      setTempStartDate(dateStr);
      setTempEndDate("");
    } else {
      setTempEndDate(dateStr);
    }
  };

  const handleApplyCalendar = () => {
    if (!tempStartDate) return;
    setFromDate(tempStartDate);
    setToDate(tempEndDate || tempStartDate);
    setShowDatePicker(false);
  };

  const handleCancelCalendar = () => {
    setTempStartDate(fromDate);
    setTempEndDate(toDate);
    setShowDatePicker(false);
  };

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
    setTempStartDate("");
    setTempEndDate("");
    setSelectedPreset("custom");
    setShowDatePicker(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        handleCancelCalendar();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [fromDate, toDate]);

  const renderCalendarMonth = (monthOffset) => {
    const targetDate = new Date(
      calendarViewDate.getFullYear(),
      calendarViewDate.getMonth() + monthOffset,
      1
    );

    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const monthName = targetDate.toLocaleString("en-US", { month: "long" });

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();

    const days = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${monthOffset}-${i}`} className="cal-day empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateForInput(new Date(year, month, day));
      const isStart = dateStr === tempStartDate;
      const isEnd = dateStr === tempEndDate;
      const isInRange =
        tempStartDate && tempEndDate && dateStr > tempStartDate && dateStr < tempEndDate;

      let dayClass = "cal-day";
      if (isStart || isEnd) dayClass += " selected";
      if (isInRange) dayClass += " in-range";

      days.push(
        <button
          key={dateStr}
          type="button"
          className={dayClass}
          onClick={() => handleDayClick(dateStr)}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="calendar-month">
        <div className="calendar-month-header">
          {monthOffset === 0 && (
            <button
              type="button"
              className="cal-nav-btn"
              onClick={() => setCalendarViewDate(new Date(year, month - 1, 1))}
            >
              &lt;
            </button>
          )}
          <span className="cal-month-title">
            {monthName} {year}
          </span>
          {monthOffset === 1 && (
            <button
              type="button"
              className="cal-nav-btn"
              onClick={() => setCalendarViewDate(new Date(year, month + 1, 1))}
            >
              &gt;
            </button>
          )}
        </div>
        <div className="cal-weekdays">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="cal-days-grid">{days}</div>
      </div>
    );
  };

  const filteredRows = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus =
        statusFilter === "ALL" || String(row.status).toUpperCase() === statusFilter;
      const matchesType =
        typeFilter === "ALL" || String(row.type || "").trim() === typeFilter;

      if (!matchesStatus || !matchesType) return false;

      if (fromDate || toDate) {
        if (!row._istDateStr) return false;
        if (fromDate && row._istDateStr < fromDate) return false;
        if (toDate && row._istDateStr > toDate) return false;
      }

      if (!query) return true;
      return row._searchableText.includes(query);
    });
  }, [rows, deferredSearch, statusFilter, typeFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const statusOptions = useMemo(() => {
    const values = new Set(rows.map((row) => String(row.status || "").toUpperCase()));
    return ["ALL", ...Array.from(values).filter(Boolean)];
  }, [rows]);

  const typeOptions = useMemo(() => {
    const values = new Set(
      rows
        .map((row) => String(row.type || "").trim())
        .filter((type) => type && type !== "-")
    );
    return ["ALL", ...Array.from(values).sort()];
  }, [rows]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, typeFilter, fromDate, toDate, pageSize]);

  useEffect(() => {
    if (!tableScrollRef.current) return;

    const updateWidth = () => {
      const tableEl = tableScrollRef.current?.querySelector("table");
      if (tableEl) setTableWidth(tableEl.scrollWidth);
    };

    updateWidth();

    const handleTableScroll = () => {
      if (topScrollRef.current && tableScrollRef.current) {
        topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
      }
    };

    const handleTopScroll = () => {
      if (topScrollRef.current && tableScrollRef.current) {
        tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
      }
    };

    const tableContainer = tableScrollRef.current;
    const topContainer = topScrollRef.current;

    if (tableContainer) tableContainer.addEventListener("scroll", handleTableScroll);
    if (topContainer) topContainer.addEventListener("scroll", handleTopScroll);
    window.addEventListener("resize", updateWidth);

    return () => {
      if (tableContainer) tableContainer.removeEventListener("scroll", handleTableScroll);
      if (topContainer) topContainer.removeEventListener("scroll", handleTopScroll);
      window.removeEventListener("resize", updateWidth);
    };
  }, [pageRows, loading]);

  const handleDownloadExcel = () => {
    const excelData = filteredRows.map((row) => {
      let bankInfo = row.bankDetails;
      if (typeof bankInfo === "object" && bankInfo !== null) {
        bankInfo = `${bankInfo.bank_name || bankInfo.bankName || ""} | A/C: ${bankInfo.account_number || bankInfo.accountNumber || ""} | IFSC: ${bankInfo.ifsc || bankInfo.ifsc_code || ""}`;
      }

      return {
        "Wallet ID": row.walletId,
        "User ID": row.userId,
        "Wallet Date": row._formattedWalletDate,
        "Type": row.type,
        "Status": String(row.status || "PENDING").toUpperCase(),
        "User Real Name": row.userRealName,
        "Display Name": row.displayName,
        "Contact Details": `${row.phoneNumber} / ${row.emailAddress}`,
        "Address": row.address,
        "Job Name": row.jobName,
        "Who Verified These Jobs?": row.verifiedBy,
        "Place of Supply": row.placeOfSupply,
        "Bank Details": bankInfo || "",
        "Actual Job Price": row.actualJobPrice ?? "-",
        "Wallet Amount (Payable)": row.walletAmountPayable ?? "-",
        "Withdrawal Request Amount": row.withdrawalRequestAmount ?? "-",
        "User Payable Amount": row.userPayableAmount ?? "-",
        "Pending Balance": row.pendingBalance ?? "-",
        "Platform Fee": row.platformFee ?? "-",
        "Taxable Amount": row.taxableAmount ?? "-",
        "CGST": row.cgst ?? "-",
        "SGST": row.sgst ?? "-",
        "IGST": row.igst ?? "-",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Wallet Withdrawals");
    XLSX.writeFile(workbook, `Wallet_Withdrawals_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const showingStart = filteredRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingEnd = Math.min(currentPage * pageSize, filteredRows.length);

  return (
    <div className="wallet-page">
      {/* HEADER */}
      <div className="wallet-page-header">
        <div>
          <h1>Wallet Withdrawals</h1>
          <p>Wallet and withdrawal payment details</p>
        </div>
        <div className="wallet-total-card">
          <span>Total Records</span>
          <strong>{filteredRows.length}</strong>
        </div>
      </div>

      {/* COMPACT FILTER TOOLBAR */}
      <div className="wallet-toolbar">
        <div className="wallet-search">
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Search wallet, user, name, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="toolbar-controls">
          {/* WALLET CALENDAR PICKER */}
          <div className="wallet-calendar-wrapper" ref={datePickerRef}>
            <button
              type="button"
              className="date-picker-trigger-input wallet-calendar-trigger"
              onClick={() => {
                if (!showDatePicker) {
                  setTempStartDate(fromDate);
                  setTempEndDate(toDate);
                  const baseDate = fromDate ? new Date(`${fromDate}T12:00:00`) : new Date();
                  setCalendarViewDate(
                    new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
                  );
                }
                setShowDatePicker((previous) => !previous);
              }}
            >
              <span>{formatDisplayRange(fromDate, toDate)}</span>
              <span className="calendar-icon">📅</span>
            </button>

            {showDatePicker && (
              <div className="calendar-popup wallet-calendar-popup">
                <div className="calendar-popup-sidebar">
                  {[
                    ["today", "Today"],
                    ["yesterday", "Yesterday"],
                    ["last7days", "Last 7 Days"],
                    ["last30days", "Last 30 Days"],
                    ["thisMonth", "This Month"],
                    ["lastMonth", "Last Month"],
                    ["custom", "Custom Range"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={selectedPreset === key ? "active" : ""}
                      onClick={() => handlePresetSelect(key)}
                    >
                      {label}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="wallet-calendar-clear"
                    onClick={handleClearDates}
                  >
                    Clear Dates
                  </button>
                </div>

                <div className="calendar-popup-main">
                  <div className="calendar-months-container">
                    {renderCalendarMonth(0)}
                    {renderCalendarMonth(1)}
                  </div>

                  <div className="calendar-popup-footer">
                    <span className="cal-range-preview">
                      {formatDisplayRange(tempStartDate, tempEndDate)}
                    </span>

                    <div className="cal-footer-actions">
                      <button
                        type="button"
                        className="cal-cancel-btn"
                        onClick={handleCancelCalendar}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        className="cal-apply-btn"
                        onClick={handleApplyCalendar}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <label htmlFor="statusSelect" style={{ marginLeft: "4px" }}>Status:</label>
          <select
            id="statusSelect"
            className="wallet-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status === "ALL" ? "All Status" : status}
              </option>
            ))}
          </select>

          <label htmlFor="typeSelect" style={{ marginLeft: "4px" }}>Type:</label>
          <select
            id="typeSelect"
            className="wallet-status-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {typeOptions.map((type) => (
              <option key={type} value={type}>
                {type === "ALL" ? "All Types" : type}
              </option>
            ))}
          </select>

          <label htmlFor="pageSizeSelect" style={{ marginLeft: "4px" }}>Records:</label>
          <select
            id="pageSizeSelect"
            className="wallet-size-filter"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={150}>150</option>
            <option value={200}>200</option>
          </select>

          <button
            type="button"
            className="wallet-excel-btn"
            onClick={handleDownloadExcel}
            title="Download all filtered records as Excel"
          >
            Download Excel
          </button>
        </div>
      </div>

      {/* TABLE CARD CONTAINER */}
      <div className="wallet-table-card">
        {/* TOP PAGINATION BAR */}
        {!loading && !error && filteredRows.length > 0 && (
          <div className="wallet-pagination top-pagination">
            <span>
              Showing <strong>{showingStart}</strong> to <strong>{showingEnd}</strong> of <strong>{filteredRows.length}</strong>
            </span>

            <div className="pagination-buttons">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="page-number">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* TOP HORIZONTAL SCROLLBAR */}
        {!loading && pageRows.length > 0 && (
          <div className="top-scrollbar-container" ref={topScrollRef}>
            <div
              className="top-scrollbar-dummy"
              style={{ width: `${tableWidth}px` }}
            />
          </div>
        )}

        {/* TABLE CONTENT */}
        {loading ? (
          <div className="wallet-state">
            <div className="wallet-spinner" />
            <span>Loading wallet withdrawals...</span>
          </div>
        ) : (
          <div className="wallet-table-scroll" ref={tableScrollRef}>
            <table className="wallet-table">
              <thead>
                <tr>
                  <th style={{ width: "110px", minWidth: "110px" }}>Wallet ID</th>
                  <th style={{ width: "110px", minWidth: "110px" }}>User ID</th>
                  <th style={{ width: "160px", minWidth: "160px" }}>Wallet Date</th>
                  <th style={{ width: "110px", minWidth: "110px" }}>Type</th>
                  <th style={{ width: "100px", minWidth: "100px" }}>Status</th>
                  <th style={{ width: "140px", minWidth: "140px" }}>User Real Name</th>
                  <th style={{ width: "130px", minWidth: "130px" }}>Display Name</th>
                  <th style={{ width: "180px", minWidth: "180px" }}>Contact Details</th>
                  <th style={{ width: "190px", minWidth: "190px" }}>Address</th>
                  <th style={{ width: "160px", minWidth: "160px" }}>Job Name</th>
                  <th style={{ width: "140px", minWidth: "140px" }}>Who Verified These Jobs?</th>
                  <th style={{ width: "130px", minWidth: "130px" }}>Place of Supply</th>
                  <th style={{ width: "180px", minWidth: "180px" }}>Bank Details</th>
                  <th className="th-money th-wrap" style={{ width: "90px", minWidth: "90px" }}>Actual Job Price</th>
                  <th className="th-money th-wrap" style={{ width: "95px", minWidth: "95px" }}>Wallet Amount (Payable)</th>
                  <th className="th-money th-wrap" style={{ width: "100px", minWidth: "100px" }}>Withdrawal Request Amount</th>
                  <th className="th-money th-wrap" style={{ width: "95px", minWidth: "95px" }}>User Payable Amount</th>
                  <th className="th-money th-wrap" style={{ width: "85px", minWidth: "85px" }}>Pending Balance</th>
                  <th className="th-money th-wrap" style={{ width: "80px", minWidth: "80px" }}>Platform Fee</th>
                  <th className="th-money th-wrap" style={{ width: "85px", minWidth: "85px" }}>Taxable Amount</th>
                  <th className="th-money th-wrap" style={{ width: "55px", minWidth: "55px" }}>CGST</th>
                  <th className="th-money th-wrap" style={{ width: "55px", minWidth: "55px" }}>SGST</th>
                  <th className="th-money th-wrap" style={{ width: "55px", minWidth: "55px" }}>IGST</th>
                </tr>
              </thead>

              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan="23" className="no-records-cell">
                      No matching records found
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, index) => (
                    <tr key={`${row.walletId}-${row.userId}-${index}`}>
                      <td>
                        {String(row.type || "").trim().toLowerCase() === "withdrawal" ? (
                          <WalletAllocationPopup
                            transaction={row}
                            walletRows={rows}
                            withdrawalRows={rows}
                          />
                        ) : (
                          <span className="id-value">{row.walletId}</span>
                        )}
                      </td>
                      <td>
                        <span className="id-value">{row.userId}</span>
                      </td>
                      <td className="date-cell">{row._formattedWalletDate}</td>
                      <td>
                        <span className={getTypeClass(row.type)}>
                          {row.type || "-"}
                        </span>
                      </td>
                      <td>
                        <span className={getStatusClass(row.status)}>
                          {String(row.status || "PENDING").toUpperCase()}
                        </span>
                      </td>
                      <td className="cell-wrap name-cell">{row.userRealName}</td>
                      <td className="cell-wrap">{row.displayName}</td>
                      <td className="cell-wrap">
                        <div className="contact-cell">
                          <span className="id-value">{row.phoneNumber}</span>
                          <span className="email-sub">{row.emailAddress}</span>
                        </div>
                      </td>
                      <td className="cell-wrap address-cell">
                        <span className="address-cell-text">{row.address}</span>
                        <AadhaarDetailsPopup
                          userId={row.userId}
                          userName={row.userRealName}
                          apiBaseUrl={API_BASE_URL}
                        />
                      </td>
                      
                        <td className="cell-wrap job-cell">
                          <span className="job-name-content">
                            <span>{row.jobName}</span>

                            {row.jobDetailId &&
                              String(row.type).trim().toLowerCase() !== "withdrawal" && (
                                <a
                                  href={`${window.location.origin}/job-detail-test/${encodeURIComponent(
                                    row.jobDetailId
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="job-detail-info-icon"
                                  title="View job details"
                                  aria-label={`View details for ${row.jobName}`}
                                >
                                  i
                                </a>
                              )}
                          </span>
                        </td>

                      <td className="cell-wrap">{row.verifiedBy}</td>
                      <td className="cell-wrap">{row.placeOfSupply}</td>
                      <td className="cell-wrap">
                        <BankDetails bank={row.bankDetails} />
                      </td>
                      <td className="money-cell">{formatMoney(row.actualJobPrice)}</td>
                      <td className="money-cell payable-cell">{formatMoney(row.walletAmountPayable)}</td>
                      <td className="money-cell">{formatMoney(row.withdrawalRequestAmount)}</td>
                      <td className="money-cell">{formatMoney(row.userPayableAmount)}</td>
                      <td className="money-cell">{formatMoney(row.pendingBalance)}</td>
                      <td className="money-cell">{formatMoney(row.platformFee)}</td>
                      <td className="money-cell">{formatMoney(row.taxableAmount)}</td>
                      <td className="money-cell">{formatMoney(row.cgst)}</td>
                      <td className="money-cell">{formatMoney(row.sgst)}</td>
                      <td className="money-cell">{formatMoney(row.igst)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* BOTTOM PAGINATION BAR */}
        {!loading && !error && filteredRows.length > 0 && (
          <div className="wallet-pagination bottom-pagination">
            <span>
              Showing <strong>{showingStart}</strong> to <strong>{showingEnd}</strong> of <strong>{filteredRows.length}</strong>
            </span>

            <div className="pagination-buttons">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="page-number">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}